import { useLaunchDarklyClient, useLogger } from "@sg-protect/federation-runtime/squide";
import {
    useHoneycombInstrumentationClient,
    useLogRocketInstrumentationClient,
    useMixpanelClient
} from "@workleap/telemetry/react";
import LogRocket from "logrocket";
import { useCallback } from "react";
import { identity } from "./identity.ts";
import { identifySessionReplayUser } from "./sessionReplay.ts";
import { MixpanelProperty } from "./tracking/mixpanelProperty.ts";

export function useUserIdentification() {
    const launchDarklyClient = useLaunchDarklyClient();
    const logger = useLogger();
    const mixpanelClient = useMixpanelClient();
    const honeycombClient = useHoneycombInstrumentationClient({
        throwOnUndefined: false
    });
    const logRocketClient = useLogRocketInstrumentationClient({
        throwOnUndefined: false
    });

    /**
     * From what i understand, we wrap LogRocket.identify in a try/catch because
     * LogRocket can sometimes throw errors if it's not properly initialized.
     * Considering that we feature switch LogRocket on/off, it's safer to wrap it.
     */
    const identifyLogRocket = useCallback(
        (uniqueId: string, extraTraits?: Record<string, string>) => {
            try {
                const shareGateTraits = logRocketClient?.createShareGateDefaultUserTraits(
                    identity.shareGateIdentification
                );

                // Accessed by name rather than spreading the returned object: its declared fields are
                // strings, but it also carries an "Is In Partner Program" field of unknown/boolean type
                // that LogRocket.identify's traits type doesn't accept, and we never set it.
                const traits: Record<string, string> | undefined = shareGateTraits
                    ? {
                          "ShareGate Account Id": shareGateTraits["ShareGate Account Id"],
                          "Microsoft User Id": shareGateTraits["Microsoft User Id"],
                          "Microsoft Tenant Id": shareGateTraits["Microsoft Tenant Id"],
                          "Workspace Id": shareGateTraits["Workspace Id"],
                          "Device Id": shareGateTraits["Device Id"],
                          "Telemetry Id": shareGateTraits["Telemetry Id"],
                          ...extraTraits
                      }
                    : extraTraits;

                LogRocket.identify(uniqueId, traits);
            } catch {
                logger.error("Failed to identify to LogRocket");
            }
        },
        [logger, logRocketClient]
    );

    const identifyMicrosoftUser = useCallback(
        (shareGateAccountId: string, microsoftUserId: string, tenantId: string) => {
            identity.setSgUserId(shareGateAccountId);
            identity.setTenantId(tenantId);
            identity.setMicrosoftId(microsoftUserId);

            void launchDarklyClient.identify(identity.ldContext);
            identifyLogRocket(identity.logRocket.uid, identity.logRocket.traits);

            mixpanelClient.setGlobalEventProperties({
                [MixpanelProperty.TenantId]: tenantId
            });

            honeycombClient?.setGlobalSpanAttributes({
                "app.user_id": shareGateAccountId,
                "app.tenant_id": tenantId,
                "app.microsoft_user_id": microsoftUserId
            });
        },
        [launchDarklyClient, identifyLogRocket, mixpanelClient, honeycombClient]
    );

    const identifyShareGateUser = useCallback(
        (shareGateAccountId: string) => {
            identity.setSgUserId(shareGateAccountId);

            void launchDarklyClient.identify(identity.ldContext);
            identifyLogRocket(identity.logRocket.uid, identity.logRocket.traits);

            mixpanelClient.setGlobalEventProperties({
                [MixpanelProperty.SGAccountId]: shareGateAccountId
            });

            identifySessionReplayUser(shareGateAccountId);

            honeycombClient?.setGlobalSpanAttributes({
                "app.user_id": shareGateAccountId
            });
        },
        [launchDarklyClient, identifyLogRocket, mixpanelClient, honeycombClient]
    );

    const identifyWorkspace = useCallback(
        (workspaceId: string) => {
            identity.setWorkspaceId(workspaceId);

            void launchDarklyClient.identify(identity.ldContext);
            identifyLogRocket(identity.logRocket.uid, identity.logRocket.traits);

            honeycombClient?.setGlobalSpanAttributes({
                "app.workspace_id": workspaceId
            });
        },
        [launchDarklyClient, identifyLogRocket, honeycombClient]
    );

    const identifyTenant = useCallback(
        (tenantId: string | null) => {
            if (tenantId) {
                identity.setTenantId(tenantId);
            } else {
                identity.clearTenantId();
            }

            void launchDarklyClient.identify(identity.ldContext);
            identifyLogRocket(identity.logRocket.uid, identity.logRocket.traits);

            mixpanelClient.setGlobalEventProperties({
                [MixpanelProperty.TenantId]: tenantId
            });

            honeycombClient?.setGlobalSpanAttributes({
                "app.tenant_id": tenantId ?? undefined
            });
        },
        [launchDarklyClient, identifyLogRocket, mixpanelClient, honeycombClient]
    );

    const identifyMicrosoftTenantRequester = useCallback(
        (requesterMicrosoftUserId: string, tenantId: string) => {
            identity.setTenantId(tenantId);
            identity.setRequesterMicrosoftUserId(requesterMicrosoftUserId);

            void launchDarklyClient.identify(identity.ldContext);
            identifyLogRocket(identity.logRocket.uid, identity.logRocket.traits);

            mixpanelClient.setGlobalEventProperties({
                [MixpanelProperty.TenantId]: tenantId
            });

            honeycombClient?.setGlobalSpanAttributes({
                "app.user_id": requesterMicrosoftUserId,
                "app.microsoft_requester_id": requesterMicrosoftUserId,
                "app.tenant_id": tenantId
            });
        },
        [launchDarklyClient, identifyLogRocket, mixpanelClient, honeycombClient]
    );

    const identifyWorkspaceRequester = useCallback(
        (requesterUserId: string, workspaceId: string) => {
            identity.setSgUserId(requesterUserId);
            identity.setWorkspaceId(workspaceId);

            void launchDarklyClient.identify(identity.ldContext);
            identifyLogRocket(identity.logRocket.uid, identity.logRocket.traits);

            mixpanelClient.setGlobalEventProperties({
                [MixpanelProperty.WorkspaceId]: workspaceId,
                [MixpanelProperty.SGAccountId]: requesterUserId
            });

            identifySessionReplayUser(requesterUserId);

            honeycombClient?.setGlobalSpanAttributes({
                "app.user_id": requesterUserId,
                "app.workspace_id": workspaceId
            });
        },
        [launchDarklyClient, identifyLogRocket, mixpanelClient, honeycombClient]
    );

    const identifyReviewer = useCallback(
        (userId: string, tenantId: string, workspaceId: string) => {
            identity.setReviewerUserId(userId);
            identity.setTenantId(tenantId);
            identity.setWorkspaceId(workspaceId);

            void launchDarklyClient.identify(identity.ldContext);
            identifyLogRocket(identity.logRocket.uid, identity.logRocket.traits);

            mixpanelClient.setGlobalEventProperties({
                [MixpanelProperty.DistinctId]: userId,
                [MixpanelProperty.TenantId]: tenantId,
                [MixpanelProperty.WorkspaceId]: workspaceId
            });

            identifySessionReplayUser(userId);

            honeycombClient?.setGlobalSpanAttributes({
                "app.reviewer_user_id": userId,
                "app.tenant_id": tenantId,
                "app.workspace_id": workspaceId
            });
        },
        [launchDarklyClient, identifyLogRocket, mixpanelClient, honeycombClient]
    );

    const identifyShareRecipient = useCallback(
        (userId: string, tenantId: string, workspaceId: string) => {
            identity.setShareRecipientUserId(userId);
            identity.setTenantId(tenantId);
            identity.setWorkspaceId(workspaceId);

            void launchDarklyClient.identify(identity.ldContext);
            identifyLogRocket(identity.logRocket.uid, identity.logRocket.traits);

            mixpanelClient.setGlobalEventProperties({
                [MixpanelProperty.DistinctId]: userId,
                [MixpanelProperty.TenantId]: tenantId,
                [MixpanelProperty.WorkspaceId]: workspaceId
            });

            identifySessionReplayUser(userId);

            honeycombClient?.setGlobalSpanAttributes({
                "app.share_recipient_user_id": userId,
                "app.tenant_id": tenantId,
                "app.workspace_id": workspaceId
            });
        },
        [launchDarklyClient, identifyLogRocket, mixpanelClient, honeycombClient]
    );

    return {
        identifyMicrosoftUser,
        identifyShareGateUser,
        identifyTenant,
        identifyWorkspace,
        identifyMicrosoftTenantRequester,
        identifyWorkspaceRequester,
        identifyReviewer,
        identifyShareRecipient
    };
}
